import type { Race, Unit } from "@sins2-atlas/game-data";
import { clsx } from "clsx";
import { useMemo } from "react";
import { abilityPointPool, pointsSpent } from "../lib/ability-points";
import { honouredPoints, type StatFrame, withLevel } from "../lib/frame";
import {
  abilityPointsControlLabel,
  CORRUPTION_STATES,
  corruptionStateLabel,
  levelControlLabel,
  loadoutControlLabel,
  techControlLabel,
} from "../lib/frame-labels";
import { BASE_LEVEL, LEVEL_COUNT } from "../lib/level";
import { carriedLoadout, itemSlotCapacity } from "../lib/loadout-index";
import { pickerRaces, techRowCount } from "../lib/tech-index";
import { AbilityPointsPicker } from "./ability-points-picker";
import {
  DropdownShell,
  OptionRow,
  TRIGGER_FOCUS_RING,
  triggerTone,
  useDropdown,
} from "./facet-dropdown";
import { LoadoutPicker } from "./loadout-picker";
import { TechPicker } from "./tech-picker";

// The stat-frame controls (Issue 08 + ADR 0007 + ADR 0009 + ADR 0011 + ADR 0012), which sit
// beside each other on every surface that shows one frame. The research axis is the "Tech" dropdown
// — the word the nav already uses, and the word the game uses; "Frame" was never one of
// either. The Corruption axis is its OWN dropdown beside it — never a band inside Tech: ADR
// 0007 made the two deliberately orthogonal, and CONTEXT.md keeps them apart because a
// Corruption is a mechanic an opponent inflicts, not something a player researches. Filing it
// under Tech would be the first place the site's vocabulary lied. Both take the whole frame
// and change only their own axis, so a page wires one handler for both.
//
// The third and fourth controls, "Items" and "Level", are the per-hull levers ADR 0012 added:
// each belongs to ONE hull in one place, so each appears only where a hull does and both wear
// the same clothing as the two beside them. The two empire-wide axes ride every unit-level
// link; these two deliberately do not.
//
// Both wear the facet dropdowns' clothing on every surface (user decision 2026-09-06). The
// Corruption axis was four segments on a page and a dropdown in a comparison column; one
// control cannot read as two different kinds of thing depending where it sits, and the
// segments were the odd shape in a toolbar of triggers.

/** The research axis as one dropdown in the Race and Class dropdowns' clothing (ADR 0011,
 * Issue 38): the trigger states what is applied without being opened — "Tech: base",
 * "Tech: fully-teched", "Tech: 7 of 39" — and opens the grouped tick panel, which is led by
 * Select all and Deselect all. Those two are not extra states: Select all IS Fully-Teched and
 * Deselect all IS Base, the set's full and empty extents.
 *
 * Whether the panel is open is transient (the palette's rule, ADR 0002: it belongs to this
 * visit, not to the link), while what is ticked lives in the URL like the rest of the frame.
 * Opening, dismissing and returning focus are the shared dropdown contract, so the Tech
 * control behaves exactly as the facet dropdowns beside it. */
export function TechDropdown({
  frame,
  races,
  mixed = false,
  label,
  onChange,
}: {
  frame: StatFrame;
  /** The Races the surface is showing, which is where the tick list starts: a roster view's
   * own Races (narrowed by the Faction Selection), or the one Race of the hull an entity page
   * or a comparison column stands on (Issue 41). The control widens this by the Races the
   * Tech Set already names, so what it counts is always what it draws (ADR 0010 amendment). */
  races: readonly Race[];
  /** This control speaks for several columns that do not agree on the research axis, so the
   * trigger says "mixed" rather than claiming one column's set for all of them (ADR 0011).
   * The panel still works: setting every column at once is exactly what a mixed axis needs. */
  mixed?: boolean;
  /** The control's accessible name where its own text does not say which column it belongs
   * to — four columns' triggers all read "Tech: base" otherwise. */
  label?: string;
  onChange: (next: StatFrame) => void;
}) {
  const dropdown = useDropdown();
  // The panel's own Races: the surface's, plus any Race the set already holds tech for. A set
  // rides every link and is race-blind, so it reaches surfaces that show none of its Races —
  // and a member with no row is one the trigger counts and the reader cannot find or untick
  // (ADR 0010 amendment, 2026-09-06). Widening HERE rather than at each call site is what
  // makes the invariant hold on all four: the one place that counts is the one that draws.
  const panelRaces = useMemo(() => pickerRaces(races, frame.tech), [races, frame.tech]);
  // The denominator is the rows the panel draws: every key a set can hold has a row, so
  // "5 of 249" names 249 rows the reader can count and the count beside it can never exceed it.
  const rowCount = useMemo(() => techRowCount(panelRaces), [panelRaces]);
  // Lit whenever research is applied, quiet on Base: the same rule that lights a facet
  // dropdown holding a selection. A mixed axis is lit too — something IS applied, just not
  // one thing.
  const applied = mixed || frame.tech !== undefined || frame.research === "fullyTeched";

  return (
    <DropdownShell
      dropdown={dropdown}
      triggerClass={clsx(triggerTone(applied), TRIGGER_FOCUS_RING)}
      label={label}
      trigger={mixed ? "Tech: mixed" : techControlLabel(frame, rowCount)}
      panel={
        <TechPicker
          panelId={dropdown.listId}
          frame={frame}
          races={panelRaces}
          onChange={onChange}
          onClose={dropdown.close}
        />
      }
    />
  );
}

/** The Corruption axis as a dropdown: the site's one shape for it, beside Tech in the same
 * clothing on the roster views, the entity page, a comparison's bulk row and each comparison
 * column's header. Single-select — there is always exactly one tier — so choosing closes the
 * list, the way the Race and Class dropdowns' single-select sibling does. */
export function CorruptionDropdown({
  frame,
  mixed = false,
  label,
  onChange,
}: {
  frame: StatFrame;
  /** This control speaks for several columns that do not agree on the Corruption axis, so the
   * trigger says "mixed" rather than claiming one column's tier for all of them (ADR 0011).
   * Every option still writes every column. */
  mixed?: boolean;
  /** The control's accessible name, where the trigger's own text does not say which column it
   * belongs to — four columns' triggers all read the same otherwise. Omitted on a page with
   * one frame, where "Corruption: none" names itself. */
  label?: string;
  onChange: (next: StatFrame) => void;
}) {
  const dropdown = useDropdown();
  return (
    <DropdownShell
      dropdown={dropdown}
      triggerClass={clsx(triggerTone(mixed || frame.corruption !== "none"), TRIGGER_FOCUS_RING)}
      label={label}
      trigger={<>Corruption: {mixed ? "mixed" : corruptionStateLabel(frame.corruption)}</>}
    >
      {CORRUPTION_STATES.map((option) => (
        <OptionRow
          key={option.value}
          option={{ token: option.value, label: option.label }}
          checked={!mixed && option.value === frame.corruption}
          onClick={() => {
            // Single-select: there is always exactly one tier, so choosing closes the list.
            dropdown.setOpen(false);
            onChange({ ...frame, corruption: option.value });
          }}
        />
      ))}
    </DropdownShell>
  );
}

/** The Loadout axis as one dropdown beside Tech and Corruption (Issue 46, ADR 0012): the
 * trigger states the fill and the cap without being opened ("Items: 2 of 4") and is lit while
 * the hull is carrying anything, and it opens the shop the game itself groups this hull's
 * items into.
 *
 * It is rendered only where a hull has Item Slots (39 of 166) — the caller decides that with
 * hasItemSlots, so a hull with no shop shows no control rather than an empty one. Whether the
 * panel is open is transient (ADR 0002), while what is equipped lives in `?items=`. */
export function LoadoutDropdown({
  unit,
  frame,
  note,
  label,
  open,
  onOpenChange,
  onEquip,
  onUnequip,
  onClearAll,
}: {
  unit: Unit;
  frame: StatFrame;
  /** What the last change did, said out loud. The panel shows it while it is open, so a
   * refusal is read where the click happened; the page shows the same sentence while the
   * panel is shut, for a change the Tech control caused. Hence the open state is the
   * caller's: exactly one of the two says it. */
  note?: string;
  /** The control's accessible name where its own text does not say which hull it belongs to —
   * four comparison columns' triggers all read "Items: 0 of 8" otherwise (Issue 52). Omitted
   * on a page with one hull, where the trigger names itself. */
  label?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEquip: (id: string) => void;
  onUnequip: (id: string) => void;
  onClearAll: () => void;
}) {
  // The open state is the caller's (see `note` above), which is what the shell's controlled
  // form is for.
  const dropdown = useDropdown({ open, onOpenChange });
  const equipped = carriedLoadout(unit, frame).size;
  const capacity = itemSlotCapacity(unit, frame);

  return (
    <DropdownShell
      dropdown={dropdown}
      triggerClass={clsx(triggerTone(equipped > 0), TRIGGER_FOCUS_RING)}
      label={label}
      trigger={loadoutControlLabel(equipped, capacity)}
      panel={
        <LoadoutPicker
          panelId={dropdown.listId}
          unit={unit}
          frame={frame}
          {...(note === undefined ? {} : { note })}
          onEquip={onEquip}
          onUnequip={onUnequip}
          onClearAll={onClearAll}
          onClose={dropdown.close}
        />
      }
    />
  );
}

/** The Ability Points axis as one dropdown beside Level (Issue 62, ADR 0015 decision 4): the
 * trigger states the pool without being opened ("Ability Points: 2 of 3", the lever's name
 * alone when nothing is spent) and is lit while anything is, and it opens the hull's
 * level-driven abilities with their Ability Level positions as buttons.
 *
 * It is rendered only on the 33 hulls that have a Level — the caller decides that from
 * `unit.leveled`, so a hull with no ladder shows no control rather than an empty one, exactly
 * as the Loadout control is absent from a hull with no Item Slots. Not inline on the entity
 * page's Abilities card: a comparison column has these controls and no ability list, so an
 * inline control could not reach one (ADR 0015 decision 4).
 *
 * Multi-select in the sense the Tech and Loadout controls are: an allocation is several
 * choices, so the panel stays open while the reader spends. Whether it is open is transient
 * (ADR 0002); the allocation itself lives in `?points=`. */
export function AbilityPointsDropdown({
  unit,
  frame,
  label,
  onChange,
}: {
  unit: Unit;
  frame: StatFrame;
  /** The control's accessible name where its own text does not say which hull it belongs to —
   * four comparison columns' triggers all read "Ability Points" otherwise (Issue 63). Omitted
   * on a page with one hull, where the trigger names itself. */
  label?: string;
  onChange: (next: StatFrame) => void;
}) {
  const dropdown = useDropdown();
  // The allocation the page is SHOWING: one the hull could not have bought folds nothing and
  // is reported by the notice, so the trigger counts nothing spent (lib/frame.ts).
  const spent = pointsSpent(honouredPoints(unit, frame));
  return (
    <DropdownShell
      dropdown={dropdown}
      label={label}
      triggerClass={clsx(triggerTone(spent > 0), TRIGGER_FOCUS_RING)}
      trigger={abilityPointsControlLabel(spent, abilityPointPool(frame.level ?? BASE_LEVEL))}
      panel={
        <AbilityPointsPicker
          panelId={dropdown.listId}
          unit={unit}
          frame={frame}
          onChange={onChange}
          onClose={dropdown.close}
        />
      }
    />
  );
}

// The ladder the game gives a ship, 1 to 10 (CONTEXT.md: Level). Built from the dataset's own
// LEVEL_COUNT rather than spelled out, so a patch that lengthens the ladder lengthens the list.
const LEVELS: readonly number[] = Array.from({ length: LEVEL_COUNT }, (_, index) => index + 1);

/** The Level axis as one dropdown beside Tech, Corruption and Items (Issue 50, ADR 0012): the
 * trigger states the level without being opened ("Level: 8") and is lit above level 1, and it
 * opens the ten rungs of the game's own ladder.
 *
 * It is rendered only on the 33 hulls that HAVE levels — every capital ship, Command Ship and
 * titan, and nobody else — and the caller decides that from `unit.leveled`, so a hull with no
 * ladder shows no control rather than a disabled one, exactly as the Loadout control is absent
 * from a hull with no Item Slots.
 *
 * Single-select, like Corruption: a ship is at exactly one level, so choosing closes the list.
 * Whether the panel is open is transient (ADR 0002); the level itself lives in `?level=`. */
export function LevelDropdown({
  frame,
  label,
  onChange,
}: {
  frame: StatFrame;
  /** The control's accessible name where its own text does not say which hull it belongs to —
   * four comparison columns' triggers all read "Level: 1" otherwise (Issue 52). Omitted on a
   * page with one hull, where the trigger names itself. */
  label?: string;
  onChange: (next: StatFrame) => void;
}) {
  const dropdown = useDropdown();
  const level = frame.level ?? BASE_LEVEL;
  return (
    <DropdownShell
      dropdown={dropdown}
      label={label}
      triggerClass={clsx(triggerTone(level > BASE_LEVEL), TRIGGER_FOCUS_RING)}
      trigger={levelControlLabel(frame.level)}
    >
      {LEVELS.map((option) => (
        <OptionRow
          key={option}
          option={{ token: String(option), label: `Level ${option}` }}
          checked={option === level}
          onClick={() => {
            dropdown.setOpen(false);
            // withLevel, not a spread: level 1 must leave the frame with no level at all, and
            // choosing any level must drop a `?level=` token that was no level (lib/frame.ts).
            onChange(withLevel(frame, option));
          }}
        />
      ))}
    </DropdownShell>
  );
}
